/* eslint-disable */
import type { Context, ErrorResponse } from "./common.pb";
import type { AppFeature, Business, Customer, FilterEntity, Offer } from "./entity.pb";
import type { AppFeatureFilter, BusinessFilter, CustomerFilter, FilterEntityFilter, OfferFilter } from "./filter.pb";
import type { JoinQuery } from "./join.pb";
import type { RankBy } from "./ranking.pb";
import type { SortBy } from "./sorting.pb";

export enum SearchField {
  /** SEARCH_FIELD_UNSPECIFIED - shouldn't ever be used - but will serve as default value for SearchField */
  SEARCH_FIELD_UNSPECIFIED = "SEARCH_FIELD_UNSPECIFIED",
  /** CUSTOMER_INFO - best match on customer name and cashtag */
  CUSTOMER_INFO = "CUSTOMER_INFO",
  /** LEGACY_KEYWORDS - until discover migrated to manual keywords */
  LEGACY_KEYWORDS = "LEGACY_KEYWORDS",
  SEMRUSH_KEYWORDS = "SEMRUSH_KEYWORDS",
  MANUAL_KEYWORDS = "MANUAL_KEYWORDS",
  /** SMS - only apply if search text is a sms */
  SMS = "SMS",
  /** EMAIL - only apply if search text is an email */
  EMAIL = "EMAIL",
  /** CASHTAG - only apply if search text is a cashtag or contains banned words */
  CASHTAG = "CASHTAG",
  /** BUSINESS_NAME - support search business name w/o space and search as you type with custom analyzer */
  BUSINESS_NAME = "BUSINESS_NAME",
  CATEGORIES = "CATEGORIES",
  FILTER_NAME = "FILTER_NAME",
  /** ORDER_ITEM_NAME - join search with order items */
  ORDER_ITEM_NAME = "ORDER_ITEM_NAME",
  /** SEMRUSH_EXPT_KEYWORDS - semrush experiment keywords, use internally for relevancy evaluation */
  SEMRUSH_EXPT_KEYWORDS = "SEMRUSH_EXPT_KEYWORDS",
}

export interface SearchEntitiesBatchRequest {
  requests?: SearchEntitiesRequest[];
  /** set of analytic tokens to track Search */
  search_analytics?: SearchAnalytics;
  /** monitoring purpose for each app surface */
  context?: Context;
  /** If Afterpay, pass in consumer_uuid if not null, if Cash App, pass in customer_token and should not be null */
  customer_uuid?: string;
}

export interface SearchEntitiesBatchResponse {
  responses?: SearchEntitiesResponse[];
}

export interface SearchEntitiesRequest {
  /** internal token to track each request/response */
  request_token?: string;
  query?: string;
  /** Default 50 */
  max_results?: number;
  /** Only used for AP Search Request */
  locale?: string;
  customer_query?: CustomerQuery;
  business_query?: BusinessQuery;
  offer_query?: OfferQuery;
  app_feature_query?: AppFeatureQuery;
  sort_by?: SortBy;
  rank_by?: RankBy;
  pagination_token?: string;
  filter_query?: FilterEntityQuery;
}

export interface SearchEntitiesResponse {
  /** internal token to track each request/response */
  request_token?: string;
  results?: SearchResultEntities | undefined;
  error?: ErrorResponse | undefined;
  pagination_token?: string;
}

export interface BusinessQuery {
  business_filter?: BusinessFilter;
  match_strategies?: MatchStrategy[];
  /** only support one offer join query for now, but leave the interface generic */
  join_queries?: JoinQuery[];
}

export interface CustomerQuery {
  customer_filter?: CustomerFilter;
  match_strategies?: MatchStrategy[];
}

export interface OfferQuery {
  offer_filter?: OfferFilter;
  match_strategies?: MatchStrategy[];
}

/** filter refers to offertab filters such as offer type, categories, not ES query filter. */
export interface FilterEntityQuery {
  filter_entity_filter?: FilterEntityFilter;
  match_strategies?: MatchStrategy[];
  includeOfferCount?: boolean;
  offer_filter?: OfferFilter;
  business_filter?: BusinessFilter;
}

export interface AppFeatureQuery {
  app_feature_filter?: AppFeatureFilter;
  match_strategies?: MatchStrategy[];
}

export interface MatchStrategy {
  /** list of field_criteria means AND condition. Only one field_criteria is supported for now */
  field_criteria?: FieldCriteria[];
}

export interface FieldCriteria {
  search_field?: SearchField;
  boost?: number;
  apply_synonym?: boolean;
}

export interface SearchAnalytics {
  /** analytic to measure funnel conversion in CashApp */
  cash_shop_flow_token?: string;
  /** analytic to measure search relevance/query abandonment in CashApp */
  cash_search_flow_token?: string;
  /** analytic http_correlation_id for Afterpay */
  afterpay_search_flow_token?: string;
}

export interface SearchResultEntities {
  entities?: SearchResultEntity[];
}

export interface SearchResultEntity {
  customer?: Customer | undefined;
  business?: Business | undefined;
  offer?: Offer | undefined;
  app_feature?: AppFeature | undefined;
  filter?: FilterEntity | undefined;
}
